import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Send, Sparkles, Loader2, Eye, CalendarClock, Trash2, Clock, CheckCircle,
  AlertTriangle, PenLine, Save, X
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/api';
import RecipientFields from './compose/RecipientFields';
import SavedDrafts from './compose/SavedDrafts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseRecipients(str) {
  const chunks = [];
  let current = '';
  let quoted = false;
  let angleDepth = 0;
  let escaped = false;

  const flush = () => {
    const chunk = current.trim();
    if (chunk) chunks.push(chunk);
    current = '';
  };

  for (const character of String(str || '')) {
    if (character === '\\' && quoted && !escaped) {
      escaped = true;
      current += character;
      continue;
    }
    if (character === '"' && !escaped) {
      quoted = !quoted;
      current += character;
      continue;
    }
    escaped = false;
    if (!quoted) {
      if (character === '<') angleDepth += 1;
      else if (character === '>' && angleDepth > 0) angleDepth -= 1;
      else if (angleDepth === 0 && (character === ',' || character === ';')) {
        flush();
        continue;
      } else if (angleDepth === 0 && /\s/.test(character)) {
        if (current.includes('>') || (current.includes('@') && !current.includes('<'))) {
          flush();
          continue;
        }
      }
    }
    current += character;
  }
  flush();

  const addresses = chunks.map(chunk => {
    const angleMatch = chunk.match(/<([^<>]+)>/);
    return (angleMatch ? angleMatch[1] : chunk).trim();
  }).filter(Boolean);
  return addresses.filter((address, index) => addresses.findIndex(item => item.toLowerCase() === address.toLowerCase()) === index);
}

const SCHED_STATUS = {
  scheduled: { label: 'Scheduled', color: 'bg-[#FBF1DC] text-[#8A6D3B]', icon: Clock },
  sent: { label: 'Sent', color: 'bg-[#E0F2F5] text-[#109AB4]', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-[#FCEEEA] text-[#A3503B]', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: 'bg-[#F0EFEA] text-[#5C5A56]', icon: X },
};

const EXAMPLE_PROMPTS = [
  'Write a warm follow-up to a partner vet clinic thanking them for their referrals this month.',
  'Draft a gentle check-in to a family whose pet was collected last week, letting them know the ashes are ready.',
  'Compose a friendly note introducing our free clay paw print keepsake.',
];

function historyTimestamp(item) {
  if (item.status === 'canceled') return item.canceled_at || item.updated_at || item.created_at || null;
  if (item.status === 'sent') return item.sent_at || item.updated_at || item.created_at || null;
  if (item.status === 'failed') return item.failed_at || item.updated_at || item.created_at || null;
  return item.updated_at || item.created_at || null;
}

export default function Compose() {
  const [activeTab, setActiveTab] = useState('compose');
  const [senders, setSenders] = useState([]);
  const [signatures, setSignatures] = useState([]);

  const [fromKey, setFromKey] = useState('info');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCopies, setShowCopies] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [signatureId, setSignatureId] = useState('linda_serrano');
  const [customName, setCustomName] = useState('');
  const [sigTouched, setSigTouched] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const [sending, setSending] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  const [scheduled, setScheduled] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [activeDraftId, setActiveDraftId] = useState(null);

  const loadScheduled = useCallback(async () => {
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/scheduled`);
      const data = await res.json();
      setScheduled(Array.isArray(data) ? data : []);
    } catch (e) { /* silent */ }
  }, []);

  const loadDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/compose/drafts`);
      const data = await res.json();
      setSavedDrafts(Array.isArray(data) ? data : []);
    } catch (e) { /* silent */ }
    setDraftsLoading(false);
  }, []);

  useEffect(() => {
    apiFetch(`${BACKEND_URL}/api/senders`).then(r => r.json()).then(setSenders).catch(() => {});
    apiFetch(`${BACKEND_URL}/api/signatures`).then(r => r.json()).then(setSignatures).catch(() => {});
    loadScheduled();
    loadDrafts();
  }, [loadScheduled, loadDrafts]);

  // Auto-select the sender's default signature unless the staff picked one manually
  useEffect(() => {
    if (sigTouched) return;
    const s = senders.find(x => x.key === fromKey);
    if (s && s.default_signature) setSignatureId(s.default_signature);
  }, [fromKey, senders, sigTouched]);

  const runAssist = async () => {
    const instruction = chatInput.trim();
    if (!instruction || chatLoading) return;
    setChatLoading(true);
    setChat(prev => [...prev, { role: 'user', content: instruction }]);
    setChatInput('');
    try {
      const recips = parseRecipients(to);
      const res = await apiFetch(`${BACKEND_URL}/api/compose/assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instruction,
          history: chat,
          recipient_email: recips[0] || '',
          current_draft: body,
          use_crm: true,
        }),
      });
      const data = await res.json();
      if (data.error) { toast.error('AI error: ' + data.error); }
      if (data.draft_text) setBody(data.draft_text);
      if (data.suggested_subject && !subject.trim()) setSubject(data.suggested_subject);
      setChat(data.history || []);
      if (showPreview) loadPreview();
    } catch (e) {
      toast.error('Failed to reach the AI assistant');
    }
    setChatLoading(false);
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/compose/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_key: fromKey, recipients: parseRecipients(to), subject,
          cc_recipients: parseRecipients(cc), bcc_recipients: parseRecipients(bcc),
          body_text: body, signature_id: signatureId, custom_name: customName || undefined,
        }),
      });
      const data = await res.json();
      setPreviewHtml(data.html || '');
      setShowPreview(true);
    } catch (e) {
      toast.error('Could not build preview');
    }
    setPreviewLoading(false);
  };

  const validate = () => {
    const recipients = parseRecipients(to);
    const ccRecipients = parseRecipients(cc);
    const bccRecipients = parseRecipients(bcc);
    if (recipients.length === 0) { toast.error('Please add at least one To recipient'); return null; }
    if (!subject.trim()) { toast.error('Please add a subject'); return null; }
    if (!body.trim()) { toast.error('The email body is empty'); return null; }
    if (signatureId === 'custom' && !customName.trim()) { toast.error('Enter a name for the custom signature'); return null; }
    return { recipients, ccRecipients, bccRecipients };
  };

  const send = async (sendAt = null) => {
    const recipientGroups = validate();
    if (!recipientGroups) return;
    setSending(true);
    setLastReceipt(null);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/compose/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_key: fromKey, recipients: recipientGroups.recipients, subject, body_text: body,
          cc_recipients: recipientGroups.ccRecipients,
          bcc_recipients: recipientGroups.bccRecipients,
          signature_id: signatureId, custom_name: customName || undefined,
          send_at: sendAt || undefined,
          draft_id: activeDraftId || undefined,
        }),
      });
      const data = await res.json();
      if (data.success && data.scheduled) {
        toast.success(`Email scheduled for ${new Date(data.send_at).toLocaleString()}`);
        setScheduleOpen(false); setScheduleAt('');
        resetForm();
        await loadScheduled();
        await loadDrafts();
        setActiveTab('scheduled');
      } else if (data.success) {
        setLastReceipt(data.receipt || null);
        toast.success(`SMTP accepted the email for ${(data.sent_to || []).length} recipient${(data.sent_to || []).length === 1 ? '' : 's'}`);
        resetForm();
        await loadDrafts();
      } else {
        toast.error(data.detail || 'Failed to send');
      }
    } catch (e) {
      toast.error('Failed to send email');
    }
    setSending(false);
  };

  const resetForm = () => {
    setTo(''); setCc(''); setBcc(''); setShowCopies(false); setSubject(''); setBody(''); setChat([]); setChatInput('');
    setShowPreview(false); setPreviewHtml(''); setCustomName('');
    setActiveDraftId(null);
  };

  const draftPayload = () => ({
    from_key: fromKey,
    recipients: parseRecipients(to),
    cc_recipients: parseRecipients(cc),
    bcc_recipients: parseRecipients(bcc),
    subject,
    body_text: body,
    signature_id: signatureId,
    custom_name: customName || undefined,
    chat_history: chat,
  });

  const saveDraft = async () => {
    if (savingDraft) return;
    if (![to, cc, bcc, subject, body, chatInput].some(value => String(value || '').trim()) && chat.length === 0) {
      toast.error('Add recipients, a subject or message before saving');
      return;
    }
    setSavingDraft(true);
    try {
      const url = activeDraftId
        ? `${BACKEND_URL}/api/compose/drafts/${activeDraftId}`
        : `${BACKEND_URL}/api/compose/drafts`;
      const response = await apiFetch(url, {
        method: activeDraftId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draftPayload()),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not save draft');
      setActiveDraftId(data.id);
      await loadDrafts();
      toast.success(activeDraftId ? 'Draft updated' : 'Draft saved');
    } catch (error) {
      toast.error(error.message || 'Could not save draft');
    }
    setSavingDraft(false);
  };

  const resumeDraft = (draft) => {
    setFromKey(draft.from_key || 'info');
    setTo((draft.recipients || []).join(', '));
    setCc((draft.cc_recipients || []).join(', '));
    setBcc((draft.bcc_recipients || []).join(', '));
    setShowCopies(Boolean((draft.cc_recipients || []).length || (draft.bcc_recipients || []).length));
    setSubject(draft.subject || '');
    setBody(draft.body_text || '');
    setSignatureId(draft.signature_id || 'linda_serrano');
    setCustomName(draft.custom_name || '');
    setChat(draft.chat_history || []);
    setChatInput('');
    setSigTouched(true);
    setActiveDraftId(draft.id);
    setShowPreview(false);
    setPreviewHtml('');
    setLastReceipt(null);
    setActiveTab('compose');
    toast.success('Saved draft resumed');
  };

  const deleteSavedDraft = async (draft) => {
    setDeletingDraftId(draft.id);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/compose/drafts/${draft.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not delete draft');
      if (activeDraftId === draft.id) resetForm();
      await loadDrafts();
      toast.success('Saved draft deleted');
    } catch (error) {
      toast.error(error.message || 'Could not delete draft');
    }
    setDeletingDraftId(null);
  };

  const startNewDraft = () => {
    resetForm();
    setSigTouched(false);
    setActiveTab('compose');
  };

  const cancelScheduled = async (id) => {
    try {
      await apiFetch(`${BACKEND_URL}/api/scheduled/${id}/cancel`, { method: 'POST' });
      toast.info('Scheduled email canceled');
      await loadScheduled();
    } catch (e) { toast.error('Could not cancel'); }
  };

  const sendScheduledNow = async (id) => {
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/scheduled/${id}/send-now`, { method: 'POST' });
      const data = await res.json();
      if (data.success) toast.success('SMTP accepted the email'); else toast.error('Failed to send');
      await loadScheduled();
    } catch (e) { toast.error('Could not send'); }
  };

  const activeScheduled = scheduled.filter(s => s.status === 'scheduled');
  const pastScheduled = scheduled
    .filter(s => s.status !== 'scheduled')
    .sort((a, b) => {
      const aTime = new Date(historyTimestamp(a) || 0).getTime();
      const bTime = new Date(historyTimestamp(b) || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, 20);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="compose-page">
      {/* Header + tabs */}
      <div className="flex items-center gap-3 mb-1.5">
        <PenLine className="w-6 h-6 sm:w-7 sm:h-7 text-primary flex-shrink-0" />
        <h1 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">Compose a New Email</h1>
      </div>
      <p className="text-sm sm:text-base text-muted-foreground mb-5">
        Write a fresh email with AI help — send now or schedule it for later.
      </p>

      <div className="flex gap-1 bg-[#F0EFEA] rounded-full p-1 w-fit mb-6">
        <button
          onClick={() => setActiveTab('compose')}
          data-testid="compose-tab"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeTab === 'compose' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          Compose
        </button>
        <button
          onClick={() => { setActiveTab('drafts'); loadDrafts(); }}
          data-testid="saved-drafts-tab"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'drafts' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Save className="w-4 h-4" /> Drafts
          {savedDrafts.length > 0 && <span className="ml-0.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5" data-testid="saved-drafts-tab-count">{savedDrafts.length}</span>}
        </button>
        <button
          onClick={() => { setActiveTab('scheduled'); loadScheduled(); }}
          data-testid="scheduled-tab"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'scheduled' ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <CalendarClock className="w-4 h-4" /> Scheduled
          {activeScheduled.length > 0 && (
            <span className="ml-0.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5">{activeScheduled.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'compose' ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form column */}
          <div className="lg:col-span-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">From</label>
                <Select value={fromKey} onValueChange={setFromKey}>
                  <SelectTrigger className="rounded-lg" data-testid="compose-from-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {senders.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Signature</label>
                <Select value={signatureId} onValueChange={(v) => { setSignatureId(v); setSigTouched(true); }}>
                  <SelectTrigger className="rounded-lg" data-testid="compose-signature-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {signatures.map(s => <SelectItem key={s.id} value={s.id}>{s.label || s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {signatureId === 'custom' && (
              <Input
                placeholder="Your name for the signature"
                value={customName}
                onChange={e => setCustomName(e.target.value)}
                className="rounded-lg"
                data-testid="compose-custom-name"
              />
            )}

            <RecipientFields
              to={to}
              cc={cc}
              bcc={bcc}
              showCopies={showCopies}
              onToggleCopies={() => setShowCopies(value => !value)}
              onToChange={setTo}
              onCcChange={setCc}
              onBccChange={setBcc}
            />

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Subject</label>
              <Input
                placeholder="Subject line"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="rounded-lg"
                data-testid="compose-subject-input"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Message</label>
              <Textarea
                placeholder="Write your email here, or ask the AI assistant on the right to draft it for you…"
                value={body}
                onChange={e => setBody(e.target.value)}
                className="rounded-lg min-h-[240px] font-sans text-sm leading-relaxed"
                data-testid="compose-body-textarea"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="rounded-full" onClick={loadPreview} disabled={previewLoading} data-testid="compose-preview-btn">
                {previewLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                Preview
              </Button>
              <Button variant="outline" className="rounded-full" onClick={saveDraft} disabled={savingDraft} data-testid="compose-save-draft-button">
                {savingDraft ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                {activeDraftId ? 'Update draft' : 'Save draft'}
              </Button>

              <div className="flex-1" />

              <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="compose-schedule-btn" disabled={sending}>
                    <CalendarClock className="w-4 h-4 mr-1.5" /> Schedule
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="end" data-testid="compose-schedule-popover">
                  <p className="text-sm font-semibold text-foreground mb-1">Schedule this email</p>
                  <p className="text-xs text-muted-foreground mb-2.5">Sends automatically at your chosen time — even if the app is closed.</p>
                  <input
                    type="datetime-local"
                    value={scheduleAt}
                    min={toLocalInputValue(new Date())}
                    onChange={e => setScheduleAt(e.target.value)}
                    data-testid="compose-schedule-datetime"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm mb-3"
                  />
                  <Button
                    size="sm"
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-accent"
                    disabled={!scheduleAt || sending}
                    onClick={() => send(new Date(scheduleAt).toISOString())}
                    data-testid="confirm-compose-schedule-btn"
                  >
                    {sending ? 'Scheduling...' : 'Schedule send'}
                  </Button>
                </PopoverContent>
              </Popover>

              <Button
                onClick={() => send()}
                disabled={sending}
                data-testid="compose-send-btn"
                className="bg-primary hover:bg-accent text-primary-foreground rounded-full shadow-sm px-6"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                {sending ? 'Sending...' : 'Send now'}
              </Button>
            </div>

            {lastReceipt && (
              <div className="border border-[#B8D8D1] bg-[#E1F1EE] rounded-lg p-4 flex items-start gap-3" data-testid="compose-delivery-confirmation">
                <CheckCircle className="w-5 h-5 text-[#176C62] mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#176C62]">SMTP accepted this email for delivery</p>
                  <p className="text-xs text-[#176C62]/80 mt-1" data-testid="compose-delivery-time">{new Date(lastReceipt.smtp_accepted_at).toLocaleString('en-AU')}</p>
                  <p className="text-xs text-[#176C62]/80 break-all mt-1" data-testid="compose-delivery-message-id">{lastReceipt.message_id}</p>
                </div>
              </div>
            )}

            {showPreview && (
              <div className="border border-border rounded-xl overflow-hidden mt-2" data-testid="compose-preview">
                <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Branded HTML preview</span>
                  <button onClick={() => setShowPreview(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close email preview" data-testid="close-compose-preview-button"><X className="w-4 h-4" /></button>
                </div>
                <iframe title="preview" srcDoc={previewHtml} sandbox="" className="w-full h-[420px] bg-white" data-testid="compose-preview-frame" />
              </div>
            )}
          </div>

          {/* AI assistant column */}
          <div className="lg:col-span-2">
            <div className="border border-border rounded-2xl bg-card flex flex-col h-full min-h-[420px]">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">AI Compose Assistant</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="compose-chat-log">
                {chat.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-3">Tell me what you&apos;d like to say and I&apos;ll draft it in the Pure Souls voice. Try:</p>
                    <div className="space-y-2">
                      {EXAMPLE_PROMPTS.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => setChatInput(p)}
                          className="block w-full text-left text-xs bg-muted hover:bg-[#E0F2F5] transition-colors rounded-lg px-3 py-2 text-foreground"
                          data-testid={`compose-example-${i}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  chat.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                        {m.role === 'assistant' ? '✍️ Draft updated' : m.content}
                      </div>
                    </div>
                  ))
                )}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl px-3.5 py-2 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Writing…
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3 border-t border-border">
                <div className="flex items-end gap-2">
                  <Textarea
                    placeholder="e.g. Make it warmer and mention our free keepsake…"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runAssist(); } }}
                    className="rounded-xl min-h-[44px] max-h-28 text-sm"
                    data-testid="compose-chat-input"
                  />
                  <Button
                    onClick={runAssist}
                    disabled={chatLoading || !chatInput.trim()}
                    className="rounded-full bg-primary hover:bg-accent text-primary-foreground h-11 w-11 p-0 flex-shrink-0"
                    data-testid="compose-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'drafts' ? (
        <SavedDrafts
          drafts={savedDrafts}
          loading={draftsLoading}
          deletingId={deletingDraftId}
          onResume={resumeDraft}
          onDelete={deleteSavedDraft}
          onNew={startNewDraft}
        />
      ) : (
        /* Scheduled tab */
        <div className="space-y-6" data-testid="scheduled-list">
          <div>
            <h2 className="text-lg font-serif font-medium text-foreground mb-3">Upcoming ({activeScheduled.length})</h2>
            {activeScheduled.length === 0 ? (
              <p className="text-sm text-muted-foreground">No emails scheduled right now.</p>
            ) : (
              <div className="space-y-3">
                {activeScheduled.map(s => <ScheduledRow key={s.id} s={s} onCancel={cancelScheduled} onSendNow={sendScheduledNow} />)}
              </div>
            )}
          </div>
          {pastScheduled.length > 0 && (
            <div>
              <h2 className="text-lg font-serif font-medium text-foreground mb-3">Recent</h2>
              <div className="space-y-3">
                {pastScheduled.map(s => <ScheduledRow key={s.id} s={s} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduledRow({ s, onCancel, onSendNow }) {
  const cfg = SCHED_STATUS[s.status] || SCHED_STATUS.scheduled;
  const Icon = cfg.icon;
  const eventTime = s.status === 'scheduled'
    ? s.send_at
    : s.status === 'canceled'
      ? (s.canceled_at || s.updated_at || s.created_at || null)
      : historyTimestamp(s);
  const timeLabel = s.status === 'scheduled' ? 'Scheduled' : s.status === 'canceled' ? 'Canceled' : s.status === 'sent' ? 'Sent' : 'Updated';
  return (
    <div className="border border-border rounded-xl p-4 bg-card" data-testid={`scheduled-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
              <Icon className="w-3 h-3" /> {cfg.label}
            </span>
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
              {s.kind === 'reply' ? 'Reply' : 'New email'}
            </span>
          </div>
          <p className="text-sm font-semibold text-foreground truncate">{s.subject || '(no subject)'}</p>
          <p className="text-xs text-muted-foreground truncate">To: {(s.recipients || []).join(', ')}</p>
          {((s.cc_recipients || []).length + (s.bcc_recipients || []).length) > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              <span data-testid={`scheduled-cc-count-${s.id}`}>Cc: {(s.cc_recipients || []).length}</span>
              <span aria-hidden="true"> · </span>
              <span data-testid={`scheduled-bcc-count-${s.id}`}>Bcc: {(s.bcc_recipients || []).length}</span>
            </p>
          )}
          {eventTime && (
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`scheduled-event-time-${s.id}`}>
              <Clock className="w-3 h-3" /> {timeLabel}: {new Date(eventTime).toLocaleString()}
            </p>
          )}
          {s.status === 'sent' && s.sent_at && <p className="text-xs text-[#176C62] mt-1" data-testid={`scheduled-accepted-at-${s.id}`}>SMTP accepted · {s.sent_folder_status === 'archived' ? 'saved in Sent' : 'Sent copy unavailable'}</p>}
          {s.error && <p className="text-xs text-[#A3503B] mt-1">Error: {s.error}</p>}
        </div>
        {s.status === 'scheduled' && onCancel && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onSendNow(s.id)} data-testid={`send-now-${s.id}`}>
              <Send className="w-3.5 h-3.5 mr-1" /> Send now
            </Button>
            <Button size="sm" variant="outline" className="rounded-full text-[#A3503B]" onClick={() => onCancel(s.id)} data-testid={`cancel-scheduled-${s.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
